import { useEffect, useRef, useState } from "react";
import "./MentiAvatar.css";

interface MentiAvatarProps {
    audioElement: HTMLAudioElement | null;
    status:
        | "idle"
        | "listening"
        | "processing"
        | "speaking";
}

type MouthState =
    | "closed"
    | "small"
    | "medium"
    | "wide";

export default function MentiAvatar({
    audioElement,
    status,
}: MentiAvatarProps) {

    const audioContextRef =
        useRef<AudioContext | null>(null);

    const analyserRef =
        useRef<AnalyserNode | null>(null);

    const sourceRef =
        useRef<MediaElementAudioSourceNode | null>(null);

    const frameRef =
        useRef<number | null>(null);


    const [volume, setVolume] =
        useState(0);

    const [mouthState, setMouthState] =
        useState<MouthState>("closed");

    const [blinking, setBlinking] =
        useState(false);


    /*
     * ==========================================
     * REAL-TIME AUDIO ANALYSIS
     * ==========================================
     */

    useEffect(() => {

        if (!audioElement) {
            return;
        }


        const AudioContextClass =
            window.AudioContext ||
            (
                window as typeof window & {
                    webkitAudioContext?: typeof AudioContext;
                }
            ).webkitAudioContext;


        if (!AudioContextClass) {
            console.error(
                "Web Audio API is not supported."
            );

            return;
        }


        /*
         * Create AudioContext
         */

        if (!audioContextRef.current) {

            audioContextRef.current =
                new AudioContextClass();
        }


        const audioContext =
            audioContextRef.current;


        /*
         * Create analyser only once
         */

        if (!sourceRef.current) {

            sourceRef.current =
                audioContext.createMediaElementSource(
                    audioElement
                );


            analyserRef.current =
                audioContext.createAnalyser();


            analyserRef.current.fftSize =
                512;


            analyserRef.current.smoothingTimeConstant =
                0.65;


            sourceRef.current.connect(
                analyserRef.current
            );


            analyserRef.current.connect(
                audioContext.destination
            );
        }


        const analyser =
            analyserRef.current;


        if (!analyser) {
            return;
        }


        const data =
            new Uint8Array(
                analyser.frequencyBinCount
            );


        /*
         * ======================================
         * ANALYSE AUDIO
         * ======================================
         */

        const analyse = () => {

            analyser.getByteFrequencyData(
                data
            );


            let total = 0;

            for (
                let i = 0;
                i < data.length;
                i++
            ) {
                total += data[i];
            }


            const average =
                total / data.length;


            /*
             * Normalize volume
             */

            const normalized =
                Math.min(
                    average / 45,
                    1
                );


            setVolume(normalized);


            /*
             * ==================================
             * MOUTH STATE
             * ==================================
             */

            if (
                !audioElement.paused &&
                !audioElement.ended
            ) {

                if (normalized < 0.08) {

                    setMouthState(
                        "closed"
                    );

                } else if (
                    normalized < 0.25
                ) {

                    setMouthState(
                        "small"
                    );

                } else if (
                    normalized < 0.50
                ) {

                    setMouthState(
                        "medium"
                    );

                } else {

                    setMouthState(
                        "wide"
                    );
                }

            } else {

                setMouthState(
                    "closed"
                );
            }


            frameRef.current =
                requestAnimationFrame(
                    analyse
                );
        };


        /*
         * Resume AudioContext
         */

        if (
            audioContext.state ===
            "suspended"
        ) {

            audioContext
                .resume()
                .catch(() => {});
        }


        /*
         * Start analysis
         */

        analyse();


        /*
         * Cleanup
         */

        return () => {

            if (
                frameRef.current !== null
            ) {

                cancelAnimationFrame(
                    frameRef.current
                );

                frameRef.current = null;
            }
        };

    }, [audioElement]);


    /*
     * ==========================================
     * NATURAL BLINKING
     * ==========================================
     */

    useEffect(() => {

        let timer:
            ReturnType<typeof setTimeout>;


        const blink = () => {

            setBlinking(true);


            setTimeout(() => {
                setBlinking(false);
            }, 120);


            timer = setTimeout(
                blink,
                2500 +
                    Math.random() * 3500
            );
        };


        timer = setTimeout(
            blink,
            2500 +
                Math.random() * 2500
        );


        return () => {
            clearTimeout(timer);
        };

    }, []);


    /*
     * ==========================================
     * RENDER
     * ==========================================
     */

    return (
        <div
            className={`menti-avatar ${status}`}
        >

            <div className="menti-character">

                <img
                    src="/menti/menti-front.png"
                    alt="Menti"
                    className="menti-image"
                />


                {/* ==========================
                    MOUTH
                =========================== */}

                <div
                    className={
                        `menti-mouth mouth-${mouthState}`
                    }
                />


                {/* ==========================
                    BLINK
                =========================== */}

                {blinking && (
                    <div className="menti-blink">

                        <span />
                        <span />

                    </div>
                )}


                {/* ==========================
                    THINKING
                =========================== */}

                {status === "processing" && (
                    <div className="menti-thinking">

                        <span />
                        <span />
                        <span />

                    </div>
                )}

            </div>


            {/* ==============================
                STATUS
            =============================== */}

            <div className="menti-status">

                {status === "listening" && (
                    <>
                        <span
                            className="status-dot listening"
                        />

                        Listening...
                    </>
                )}


                {status === "processing" && (
                    <>
                        <span
                            className="status-dot thinking"
                        />

                        Thinking...
                    </>
                )}


                {status === "speaking" && (
                    <>
                        <span className="voice-bars">

                            <i />
                            <i />
                            <i />
                            <i />

                        </span>

                        Menti is speaking...
                    </>
                )}

            </div>


            {/* =================================
                TEMP DEBUG
            ================================== */}

            {status === "speaking" && (
                <div
                    style={{
                        position: "absolute",
                        top: 5,
                        left: "50%",
                        transform:
                            "translateX(-50%)",
                        background:
                            "rgba(0,0,0,.75)",
                        color: "white",
                        padding:
                            "4px 8px",
                        borderRadius: 6,
                        fontSize: 11,
                        zIndex: 100,
                    }}
                >
                    {mouthState} :{" "}
                    {volume.toFixed(2)}
                </div>
            )}

        </div>
    );
}